"use client";
import HighlightedText from "./HighlightedText";
import { useEffect, useLayoutEffect, useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ReactGA from "react-ga4";
import { getStrapiMedia } from "../lib/api-helpers";

gsap.registerPlugin(ScrollTrigger);

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    titleHomeHero: string;
    picture: Picture;
    heroFeatures: any[];
  };
}

export default function HomeHero({ data }: HeroProps) {
  const imgRef = useRef<HTMLDivElement>(null);
  const imgUrl = getStrapiMedia(data?.picture?.data?.attributes?.url);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      const measurementId = process.env.NEXT_PUBLIC_MEASUREMENT_ID;
      measurementId ? ReactGA.initialize(measurementId) : "";
      ReactGA.send({
        hitType: "pageview",
        page: "/",
        title: "Default",
      });
    }, 500);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (imgRef.current) {
        const grayCircle = imgRef.current.querySelectorAll(".grayCircle");
        const lightBlue = imgRef.current.querySelectorAll(".lightBlue");
        const darkBlue = imgRef.current.querySelectorAll(".darkBlue");

        if (!lightBlue.length && !darkBlue.length && !grayCircle.length) {
          return;
        }
        const tl = gsap.timeline({ defaults: { ease: "power3.inOut" } });

        tl.fromTo(
          lightBlue,
          { y: -500, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: "slow(0.4, 0.4, false)",
            delay: 0.1,
          }
        )
          .fromTo(
            darkBlue,
            { y: -500, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              ease: "slow(0.4, 0.4, false)",
              delay: 0.2,
            }
          )
          .fromTo(
            grayCircle,
            { y: -500, opacity: 0 },
            {
              y: -83.98,
              opacity: 1,
              duration: 0.6,
              ease: "slow(0.4, 0.4, false)",
            }
          );

        ScrollTrigger.create({
          trigger: imgRef.current,
          start: "top 40%",
          end: "bottom 10%",
          animation: tl,
          onEnter: () => tl.play(),
          onLeaveBack: () => tl.reverse(),
          // markers: true
        });
      }
    }, 500); // slight delay to ensure elements are mounted

    return () => clearTimeout(timer);
  }, []);

  function addStyle() {
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "y mandatory";
    htmlElement.style.scrollPaddingTop = "65px";
    htmlElement.style.scrollBehavior = "smooth";
    htmlElement.style.scrollSnapStop = "always";
  }

  function removeStyle() {
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "";
    htmlElement.style.scrollPaddingTop = "";
    htmlElement.style.scrollBehavior = "";
    htmlElement.style.scrollSnapStop = "";
  }

  useLayoutEffect(() => {
    return () => {
      window.scrollTo(0, 0);
      removeStyle();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isTop = window.scrollY === 0;
      const isBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight;

      if (isTop || isBottom) {
        removeStyle();
      } else {
        addStyle();
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <section
      ref={imgRef}
      className='w-full bg-white flex items-center justify-center mx-auto height-100 herosection pt-[48px] pb-[36px]  px-[24px]'>
        <h2 className='sr-only'>Hero Section</h2>
      <article className='grid grid-cols-1 sm:grid-cols-2 container'>
        <h2 className='sr-only'>Hero Article</h2>
        <section className='flex flex-col items-start justify-center'>
          <HighlightedText
            text={data.titleHomeHero}
            tag='h1'
            className='!leading-[1.2] text-5xl 2xl:text-6xl font-bold text-[#032D60]'
          />
          <ul className='flex flex-col items-start mt-[32px]'>
            {data.heroFeatures.map(({ inputText }, index) => {
              return (
                <>
                  <li
                    className='px-4 py-[5px] !leading-[1] text-2xl lg:text-[22px] border-s-[1px] md:border-s-[3px] border-[#5ec4ff] mb-[16px] text-[#001630]'
                    key={index}>
                    {inputText}
                  </li>
                </>
              );
            })}
          </ul>
        </section>
        <section className='w-full relative'>
          <h2 className='sr-only'>Qualitia Home</h2>
          <Image
            src={imgUrl ?? ""}
            alt="Qualitia Home"
            width={800}
            height={800}
            className="w-full h-auto object-contain mx-auto relative lg:-top-[5%]"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 546px"
            quality={70}
            priority={true}
          />
        </section>
      </article>
    </section>
  );
}
